/**
 * PictView-Retold-Profile
 *
 * The SHARED self-service profile view. It renders into the shell workspace and reads its
 * shape from the server: GET <prefix>Profile returns the caller's own Account and persona
 * plus the field lists, so the form is built from the server's contract rather than a
 * hard-coded field set. Save issues PUT <prefix>Profile with only the editable fields.
 *
 * It is registered by the chassis and reached at the /Profile route; an app opts in by
 * enabling the profile routes on the server and adding a navigation link. When the server
 * has not mounted the routes the view shows a plain "not available" message instead of an
 * empty form.
 *
 * Theme: every themed background is paired with a themed foreground, and inputs and the
 * save button set their own background/foreground as a pair, so the view reads correctly
 * in light and dark and degrades to sane fallbacks in an unthemed host.
 */

const libPictView = require('pict-view');

function escapeHTML(pText)
{
	return String(pText === null || typeof pText === 'undefined' ? '' : pText)
		.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// "DisplayName" -> "Display Name", "LoginID" -> "Login ID", "Phone" -> "Phone".
function humanizeField(pField)
{
	return String(pField || '').replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/\bI D\b/g, 'ID');
}

class PictViewRetoldProfile extends libPictView
{
	_apiPrefix()
	{
		return this.options.APIPrefix || '/1.0/';
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap && typeof this.pict.CSSMap.injectCSS === 'function') { this.pict.CSSMap.injectCSS(); }
		this._load();
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}

	// Fetch the caller's profile and build the form. A 401/404/500 leaves a plain message
	// in the card rather than a broken form.
	_load()
	{
		if (typeof document === 'undefined' || typeof fetch !== 'function') { return Promise.resolve(null); }
		let tmpForm = document.getElementById('Retold-Profile-Form');
		let tmpUnavailable = document.getElementById('Retold-Profile-Unavailable');
		return fetch(this._apiPrefix() + 'Profile', { credentials: 'include', headers: { 'Accept': 'application/json' } })
			.then((pResponse) => pResponse.json().then((pJSON) => ({ status: pResponse.status, json: pJSON })).catch(() => ({ status: pResponse.status, json: null })))
			.then((pResult) =>
			{
				if (pResult.status !== 200 || !pResult.json)
				{
					if (tmpForm) { tmpForm.hidden = true; }
					if (tmpUnavailable)
					{
						tmpUnavailable.hidden = false;
						tmpUnavailable.textContent = (pResult.status === 401)
							? 'Please sign in to manage your profile.'
							: 'Profile management is not available for this application.';
					}
					return null;
				}
				this._profile = pResult.json;
				if (tmpUnavailable) { tmpUnavailable.hidden = true; }
				if (tmpForm) { tmpForm.hidden = false; }
				this._renderForm();
				return this._profile;
			})
			.catch(() => null);
	}

	// Build the Account and persona field inputs from the server's field contract, then wire
	// the submit handler once.
	_renderForm()
	{
		if (typeof document === 'undefined' || !this._profile) { return; }
		let tmpFields = this._profile.Fields || {};

		this._paintFields('Retold-Profile-Account-Fields', this._profile.Account || {}, tmpFields.Account || [], tmpFields.AccountReadOnly || [], 'Account');

		let tmpPersonaSection = document.getElementById('Retold-Profile-Persona');
		if (this._profile.Persona && ((tmpFields.Persona || []).length || (tmpFields.PersonaReadOnly || []).length))
		{
			if (tmpPersonaSection) { tmpPersonaSection.hidden = false; }
			this._paintFields('Retold-Profile-Persona-Fields', this._profile.Persona, tmpFields.Persona || [], tmpFields.PersonaReadOnly || [], 'Persona');
		}
		else if (tmpPersonaSection)
		{
			tmpPersonaSection.hidden = true;
		}

		let tmpForm = document.getElementById('Retold-Profile-Form');
		if (tmpForm && tmpForm.getAttribute('data-retold-wired') !== '1')
		{
			tmpForm.setAttribute('data-retold-wired', '1');
			tmpForm.addEventListener('submit', (pEvent) =>
			{
				if (pEvent && typeof pEvent.preventDefault === 'function') { pEvent.preventDefault(); }
				this.submit();
			});
		}
		this._setStatus('', '');
	}

	// One labeled input per field. Read-only fields render disabled and muted. The data-group
	// and data-field attributes let _collect read the editable values back out.
	_paintFields(pContainerID, pValues, pEditable, pReadOnly, pGroup)
	{
		let tmpContainer = document.getElementById(pContainerID);
		if (!tmpContainer) { return; }
		let fInput = (pField, pReadonly) =>
		{
			let tmpValue = (pValues && typeof pValues[pField] !== 'undefined' && pValues[pField] !== null) ? pValues[pField] : '';
			let tmpWide = (pField === 'Email' || pField === 'Contact') ? ' full' : '';
			return '<label class="retold-profile-field' + tmpWide + '">' +
				'<span>' + escapeHTML(humanizeField(pField)) + '</span>' +
				'<input type="text" data-group="' + escapeHTML(pGroup) + '" data-field="' + escapeHTML(pField) + '"' +
				(pReadonly ? ' readonly' : '') + ' value="' + escapeHTML(tmpValue) + '">' +
				'</label>';
		};
		let tmpHTML = '';
		(pReadOnly || []).forEach((pField) => { tmpHTML += fInput(pField, true); });
		(pEditable || []).forEach((pField) => { tmpHTML += fInput(pField, false); });
		tmpContainer.innerHTML = tmpHTML;
	}

	// Read the editable inputs back into { Account: {...}, Persona: {...} }.
	_collect()
	{
		let tmpResult = { Account: {}, Persona: {} };
		if (typeof document === 'undefined') { return tmpResult; }
		let tmpInputs = document.querySelectorAll('#Retold-Profile-Form input[data-field]:not([readonly])');
		Array.prototype.forEach.call(tmpInputs, (pInput) =>
		{
			let tmpGroup = pInput.getAttribute('data-group');
			let tmpField = pInput.getAttribute('data-field');
			if (tmpResult[tmpGroup]) { tmpResult[tmpGroup][tmpField] = pInput.value; }
		});
		return tmpResult;
	}

	_setStatus(pMessage, pKind)
	{
		if (typeof document === 'undefined') { return; }
		let tmpStatus = document.getElementById('Retold-Profile-Status');
		if (!tmpStatus) { return; }
		tmpStatus.textContent = pMessage ? String(pMessage) : '';
		tmpStatus.className = 'retold-profile-status' + (pKind ? (' ' + pKind) : '');
	}

	_toast(pMessage, pType)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && typeof tmpModal.toast === 'function') { tmpModal.toast(pMessage, { type: pType || 'info' }); }
	}

	/**
	 * Save the editable fields. Exposed so a test (or an app) can drive it without a DOM
	 * submit event. Returns the promise so callers can await the result.
	 */
	submit()
	{
		if (typeof document === 'undefined' || typeof fetch !== 'function') { return Promise.resolve(null); }
		let tmpBody = this._collect();
		this._setStatus('Saving...', 'ok');
		return fetch(this._apiPrefix() + 'Profile',
			{
				method: 'PUT', credentials: 'include',
				headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
				body: JSON.stringify(tmpBody)
			})
			.then((pResponse) => pResponse.json().then((pJSON) => ({ status: pResponse.status, json: pJSON })).catch(() => ({ status: pResponse.status, json: null })))
			.then((pResult) =>
			{
				if (pResult.status === 200 && pResult.json)
				{
					this._profile = pResult.json;
					this._renderForm();
					this._setStatus('Saved.', 'ok');
					this._toast('Profile saved.', 'success');
					return pResult.json;
				}
				let tmpMessage = (pResult.json && pResult.json.Error) || 'Could not save your profile.';
				this._setStatus(tmpMessage, 'error');
				this._toast(tmpMessage, 'error');
				return null;
			})
			.catch(() =>
			{
				this._setStatus('Could not save your profile.', 'error');
				this._toast('Could not save your profile.', 'error');
				return null;
			});
	}
}

module.exports = PictViewRetoldProfile;
module.exports.default_configuration =
{
	ViewIdentifier: 'Retold-Profile',
	DefaultRenderable: 'Retold-Profile',
	DefaultDestinationAddress: '#PRSP_Container',
	AutoRender: false,
	// Every themed background is paired with a themed foreground. Inputs and the save button
	// do not inherit color from an ancestor, so they set both as a pair; read-only inputs use
	// the muted pair so they read as non-editable in either mode.
	CSS: /*css*/ `
		.retold-profile { padding: 24px; color: var(--theme-color-text-primary, #1f2430); }
		.retold-profile-card { max-width: 640px; margin: 0 auto; padding: 24px 28px; border-radius: 10px;
			background: var(--theme-color-background-panel, #ffffff);
			color: var(--theme-color-text-primary, #1f2430);
			border: 1px solid var(--theme-color-border-default, #d6d9de); }
		.retold-profile-title { font-size: 20px; margin: 0 0 4px 0; color: inherit; }
		.retold-profile-hint { margin: 0 0 18px 0; font-size: 13px; color: var(--theme-color-text-muted, #6b7280); }
		.retold-profile-unavailable { margin: 8px 0 0 0; font-size: 14px; color: var(--theme-color-text-secondary, #55606f); }
		.retold-profile-section { margin-bottom: 22px; }
		.retold-profile-subhead { font-size: 12px; text-transform: uppercase; letter-spacing: 0.05em; margin: 0 0 10px 0;
			color: var(--theme-color-text-secondary, #55606f); }
		.retold-profile-fields { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px 16px; }
		.retold-profile-field { display: flex; flex-direction: column; gap: 4px; font-size: 13px; color: inherit; }
		.retold-profile-field.full { grid-column: 1 / -1; }
		.retold-profile-field input { padding: 8px 10px; border-radius: 6px; font-size: 14px;
			background: var(--theme-color-background-primary, #ffffff);
			color: var(--theme-color-text-primary, #1f2430);
			border: 1px solid var(--theme-color-border-default, #d6d9de); }
		.retold-profile-field input[readonly] { background: var(--theme-color-background-secondary, #f0f2f5);
			color: var(--theme-color-text-muted, #6b7280); cursor: not-allowed; }
		.retold-profile-actions { display: flex; align-items: center; gap: 14px; margin-top: 6px; }
		.retold-profile-save { padding: 9px 16px; border: 0; border-radius: 6px; cursor: pointer; font-size: 14px;
			background: var(--theme-color-brand-primary, #3a6ea5); color: var(--theme-color-text-on-brand, #ffffff); }
		.retold-profile-status { font-size: 13px; color: var(--theme-color-text-muted, #6b7280); }
		.retold-profile-status.ok { color: var(--theme-color-text-secondary, #55606f); }
		.retold-profile-status.error { color: var(--theme-color-status-error, #c0392b); }
		@media (max-width: 560px) { .retold-profile-fields { grid-template-columns: 1fr; } }
	`,
	Templates:
	[
		{
			Hash: 'Retold-Profile',
			Template: '<div class="retold-profile"><div class="retold-profile-card">' +
				'<h1 class="retold-profile-title">My Profile</h1>' +
				'<p class="retold-profile-hint">Your name and contact details, and how you appear in the current space.</p>' +
				'<p id="Retold-Profile-Unavailable" class="retold-profile-unavailable" hidden></p>' +
				'<form id="Retold-Profile-Form" class="retold-profile-form" hidden>' +
					'<section class="retold-profile-section">' +
						'<h2 class="retold-profile-subhead">Account</h2>' +
						'<div id="Retold-Profile-Account-Fields" class="retold-profile-fields"></div>' +
					'</section>' +
					'<section id="Retold-Profile-Persona" class="retold-profile-section" hidden>' +
						'<h2 class="retold-profile-subhead">This space</h2>' +
						'<div id="Retold-Profile-Persona-Fields" class="retold-profile-fields"></div>' +
					'</section>' +
					'<div class="retold-profile-actions">' +
						'<button type="submit" class="retold-profile-save">Save changes</button>' +
						'<span id="Retold-Profile-Status" class="retold-profile-status"></span>' +
					'</div>' +
				'</form>' +
				'</div></div>'
		}
	],
	Renderables:
	[
		{ RenderableHash: 'Retold-Profile', TemplateHash: 'Retold-Profile', ContentDestinationAddress: '#PRSP_Container', RenderMethod: 'replace' }
	]
};
